/**
 * The masthead search overlay.
 *
 * THE TRIGGER IS A REAL LINK. `[data-search-open]` is an <a> pointing at
 * /newsstudios/search — the advanced-search page. This function intercepts its
 * click and opens the dialog instead. Nothing here is required for the search
 * to be reachable: with the bundle blocked, still loading, or thrown out by the
 * mod() guard, the magnifier is a link to a working search page. The previous
 * <details> implementation was chosen for exactly that no-JS property, but paid
 * for it with a broken keyboard path (see below) and a position:fixed hack to
 * keep the panel on screen at 375px. A link is a better fallback than either.
 *
 * WHY showModal() AND NOT A CLASS TOGGLE. Four behaviours come for free and all
 * four were missing or hand-rolled before:
 *
 *   - Escape closes it. Native, and it fires `cancel` then `close`.
 *   - Focus is trapped inside the dialog.
 *   - The rest of the document goes inert — no tabbing to the page behind.
 *   - `autofocus` inside the dialog is honoured ON OPEN.
 *
 * That last one is the bug this replaced. Clicking a <summary> leaves focus on
 * the summary, so the field was never focused and Enter re-toggled the
 * disclosure rather than submitting — the search could not be used from the
 * keyboard at all, and with a mouse it took a second click to reach the field.
 *
 * WHAT IS STILL OURS: the backdrop click, the body scroll lock, and restoring
 * focus to the trigger. Everything else is the platform's.
 */
import { qs, qsa, on } from './dom.js';

export function setupSearch() {
  const dialog = qs('[data-search-dialog]');
  const openers = qsa('[data-search-open]');
  if (!dialog || !openers.length) return;

  // Feature-detect rather than assume. <dialog> has been everywhere since 2022,
  // but if it is missing showModal() is undefined and the click handler would
  // throw on every tap — leaving the user with a magnifier that does nothing.
  // Returning early keeps the <a> a plain link to the search page.
  if (typeof dialog.showModal !== 'function') return;

  const field = qs('[data-search-field]', dialog);

  // Where to send focus on close. Tracked because the overlay can be opened
  // from the bar or, on a phone, from the drawer, and returning focus to the
  // wrong one puts the caret somewhere invisible.
  let lastOpener = null;

  function open(opener) {
    lastOpener = opener;
    dialog.showModal();

    // Locks the page behind the overlay. showModal() prevents interaction but
    // not scrolling: without this the page still scrolls under the scrim on
    // touch, which looks like the overlay is sliding. Declared in main.css via
    // @source inline() — no template contains it.
    document.body.classList.add('overflow-hidden');

    // `autofocus` already put the caret in the field. This is only about a
    // REPEAT search: coming back from a results page the field is pre-filled
    // with the previous query, and selecting it means typing replaces it
    // instead of appending to it.
    if (field && field.value) field.select();
  }

  for (const opener of openers) {
    on(opener, 'click', (event) => {
      // Ctrl/Cmd/middle-click means "open the search page in a new tab", which
      // is a legitimate thing to want from a link. Let the browser have it.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      open(event.currentTarget);
    });
  }

  for (const closer of qsa('[data-search-close]', dialog)) {
    on(closer, 'click', () => dialog.close());
  }

  /*
   * Backdrop click.
   *
   * A click on the scrim targets the DIALOG ITSELF — the ::backdrop is not in
   * the tree and cannot be a target. So `target === dialog` is precisely "the
   * click landed outside the panel", and it works only because the dialog is
   * sized to the whole viewport with the panel laid out inside it (see
   * .search-overlay in components.css). Size the dialog to the panel and this
   * test degrades to a 1px frame nobody can hit.
   */
  on(dialog, 'click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  // One handler for every way out — the close button, Escape, the backdrop.
  // `close` is the only event all three end at.
  on(dialog, 'close', () => {
    document.body.classList.remove('overflow-hidden');
    lastOpener?.focus?.();
  });

  /*
   * An empty submit.
   *
   * The field is `required`, so the browser blocks this and shows its own
   * bubble — but a value of only spaces passes `required` and would send the
   * user to a search for nothing. Trim it here rather than validate:
   * a search box that silently fixes " ایران " is doing the right thing.
   */
  const form = qs('form', dialog);
  if (form && field) {
    on(form, 'submit', (event) => {
      field.value = field.value.trim();
      if (field.value === '') {
        event.preventDefault();
        field.focus();
      }
    });
  }
}
